import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Mail,
  Instagram,
  Music2,
  Youtube,
  Send,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

import { ARTIST } from "@/lib/constants";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline monochrome icons
const SpotifyIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0Zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02Zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2Zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.42-1.02.599-1.559.3Z"/>
  </svg>
);
const BeatportIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0Zm-1.176 4.5h1.99v6.1l3.62-2.09.995 1.725-4.615 2.665v.15l4.616 2.664-.996 1.725-3.62-2.09v6.1h-1.99v-6.1L7.204 17.44l-.995-1.725 4.615-2.665v-.15L6.209 10.235l.996-1.725 3.619 2.09V4.5Z"/>
  </svg>
);
const SoundcloudIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M7 8v10h1V8H7Zm2 0v10h1V8H9Zm2 0v10h1V8h-1Zm2 0v10h1V8h-1Zm2 0v10h1V8h-1Zm2 .5v9.5h1V8.5h-1Zm2 0v9.5h1V8.5h-1Zm2 .5c-.6 0-1.1.2-1.6.5V18h5c1.4 0 2.5-1.1 2.5-2.5S22.9 13 21.5 13h-.7c-.1-2.2-1.9-4-4.1-4-.5 0-1 .1-1.5.3.1.5.2 1.1.2 1.7v6H21c.3 0 .5.2.5.5s-.2.5-.5.5h-2v-8h.5Zm-14 .5v8h1V9h-1Zm-2 1v6h1v-6h-1Zm-2 1v4h1v-4H3Z"/>
  </svg>
);

const socials = [
  {
    id: "spotify",
    label: "Spotify",
    handle: "Irwan Craw",
    Icon: SpotifyIcon,
    href: ARTIST.spotifyArtistUrl,
  },
  {
    id: "instagram",
    label: "Instagram",
    handle: "@irwancraw",
    Icon: (p) => <Instagram size={p.size ?? 14} />,
    href: ARTIST.instagramUrl,
  },
  {
    id: "youtube",
    label: "YouTube",
    handle: "@irwancraw",
    Icon: (p) => <Youtube size={p.size ?? 14} />,
    href: ARTIST.youtubeChannelUrl,
  },
  {
    id: "beatport",
    label: "Beatport",
    handle: "Irwan Craw",
    Icon: BeatportIcon,
    href: ARTIST.beatportArtist,
  },
  {
    id: "soundcloud",
    label: "SoundCloud",
    handle: "@irwancraw",
    Icon: SoundcloudIcon,
    href: ARTIST.soundcloudUrl,
  },
];

const initialForm = {
  name: "",
  email: "",
  country: "",
  city: "",
  event_type: "",
  event_date: "",
  location: "",
  budget: "",
  message: "",
};

const inputClass =
  "bg-transparent border-[color:var(--line-strong)] text-ink placeholder:text-muted/60 focus-visible:ring-[color:var(--gold)] transition";

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type || !form.message) {
      toast.error("Please fill in name, email, event type and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/booking`, form);
      toast.success("Booking inquiry sent. We'll get back to you soon.");
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong.";
      toast.error(
        typeof detail === "string" ? detail : "Could not send inquiry."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="contact-section"
      className="relative w-full bg-bg py-24 md:py-32 border-t border-[color:var(--line)] overflow-hidden"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 100% 100%, rgba(201,169,97,0.12), transparent 55%)",
        }}
      />

      <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left column: intro + socials */}
          <motion.div
            variants={stagger(0.1, 0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="lg:col-span-5"
          >
            <motion.p
              variants={fadeUp}
              className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold"
            >
              <span className="h-px w-8 bg-[color:var(--gold)]/70" />
              Chapter 10 — Booking
            </motion.p>
            <motion.h2
              variants={fadeUp}
              data-testid="contact-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-ink"
            >
              Book{" "}
              <span className="font-editorial text-gold-hi">Irwan Craw</span>
            </motion.h2>

            <motion.p
              variants={fadeUp}
              className="mt-6 max-w-md font-editorial text-xl text-ink/85"
            >
              For clubs, beach clubs, festivals, luxury venues and
              international bookings.
            </motion.p>

            <motion.p
              variants={fadeUp}
              className="mt-4 max-w-md text-sm text-muted leading-relaxed"
            >
              Bookings, collabs, sync licensing & press — drop a message or
              reach out directly.
            </motion.p>

            <motion.a
              variants={fadeUp}
              data-testid="contact-email-link"
              href={`mailto:${ARTIST.email}`}
              className="group mt-8 inline-flex items-center gap-3 rounded-full border border-[color:var(--gold)]/40 bg-transparent px-5 py-3 text-[11px] font-bold uppercase tracking-[0.24em] text-[color:var(--gold-hi)] transition-all duration-500 hover:bg-[color:var(--gold)] hover:text-[#0a0a0a]"
            >
              <Mail size={14} />
              {ARTIST.email}
            </motion.a>

            {/* Socials */}
            <motion.div
              variants={stagger(0.08, 0.1)}
              className="mt-10 grid grid-cols-1 gap-2"
            >
              <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gold mb-2">
                Follow the journey
              </p>
              {socials.map((s) => (
                <motion.a
                  key={s.id}
                  variants={fadeUp}
                  data-testid={`social-link-${s.id}`}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="group relative flex items-center justify-between overflow-hidden rounded-sm border border-[color:var(--line)] bg-bg-2 px-5 py-4 transition-all duration-500 hover:border-[color:var(--gold)]/50 hover:bg-[color:var(--gold)]/[0.04]"
                >
                  <div className="flex items-center gap-4">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[color:var(--gold)]/40 text-gold transition-all duration-500 group-hover:bg-[color:var(--gold)] group-hover:text-[#0a0a0a] group-hover:border-[color:var(--gold)]">
                      <s.Icon size={12} />
                    </span>
                    <div>
                      <p className="text-[9px] font-bold uppercase tracking-[0.28em] text-muted">
                        {s.label}
                      </p>
                      <p className="text-sm font-semibold text-ink">
                        {s.handle}
                      </p>
                    </div>
                  </div>
                  <span className="text-[color:var(--gold-hi)] opacity-0 -translate-x-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-x-0">
                    →
                  </span>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>

          {/* Right column: booking form */}
          <motion.div
            variants={stagger(0.1, 0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="lg:col-span-7"
          >
            <motion.form
              variants={fadeUp}
              data-testid="booking-form"
              onSubmit={submitBooking}
              className="relative overflow-hidden rounded-sm border border-[color:var(--line)] bg-bg-2 p-8 md:p-10"
            >
              {/* Corner mark */}
              <span className="pointer-events-none absolute right-4 top-4 font-editorial text-lg text-[color:var(--gold-hi)]/60">
                ✦
              </span>

              <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gold">
                Booking Inquiry
              </p>
              <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-ink">
                Tell us about your event
              </h3>
              <p className="mt-2 font-editorial text-base text-[color:var(--gold-hi)]/85">
                All fields help us craft the right proposal.
              </p>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
                    Name
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="booking-input-name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your full name"
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="booking-input-email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@company.com"
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country" className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
                    Country
                  </Label>
                  <Input
                    id="country"
                    name="country"
                    data-testid="booking-input-country"
                    value={form.country}
                    onChange={onChange}
                    placeholder="Spain, Mexico, France…"
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
                    City
                  </Label>
                  <Input
                    id="city"
                    name="city"
                    data-testid="booking-input-city"
                    value={form.city}
                    onChange={onChange}
                    placeholder="Ibiza, Tulum, Mykonos…"
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event_type" className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
                    Event Type
                  </Label>
                  <Input
                    id="event_type"
                    name="event_type"
                    data-testid="booking-input-event-type"
                    value={form.event_type}
                    onChange={onChange}
                    placeholder="Club, beach club, festival…"
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event_date" className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
                    Date
                  </Label>
                  <Input
                    id="event_date"
                    name="event_date"
                    data-testid="booking-input-event-date"
                    value={form.event_date}
                    onChange={onChange}
                    placeholder="e.g. 2026-08-12"
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location" className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
                    Venue
                  </Label>
                  <Input
                    id="location"
                    name="location"
                    data-testid="booking-input-location"
                    value={form.location}
                    onChange={onChange}
                    placeholder="Venue name"
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget" className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
                    Budget
                  </Label>
                  <Input
                    id="budget"
                    name="budget"
                    data-testid="booking-input-budget"
                    value={form.budget}
                    onChange={onChange}
                    placeholder="e.g. €5,000 — €10,000"
                    className={inputClass}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="message" className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted">
                    Message
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="booking-input-message"
                    value={form.message}
                    onChange={onChange}
                    placeholder="Tell us about the vibe, timing, expected crowd…"
                    rows={5}
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="booking-submit-button"
                disabled={loading}
                className="mt-8 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-hi)] text-[#0a0a0a] px-8 py-6 text-[11px] font-bold tracking-[0.28em] uppercase transition-all duration-500 hover:shadow-[0_0_35px_rgba(201,169,97,0.4)] disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send size={14} className="mr-2" />
                    Send Inquiry
                  </>
                )}
              </Button>
            </motion.form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
